import {
    decodeRequestOptions,
    serializeAssertionCredential,
    supportsPasskeys,
} from '@wolliware/wollipeople/webauthn';

const DEFAULT_UNRECOVERABLE_MESSAGE = 'Your session expired and this draft cannot be submitted automatically. Copy your changes before leaving this page.';
const STORAGE_PREFIX = 'wolliwords.draft-recovery.';

function storageKey(contentId) {
    return `${STORAGE_PREFIX}${contentId}`;
}

function snapshotToEntries(formData) {
    const entries = [];
    let filesDropped = false;

    for (const [key, value] of formData.entries()) {
        if (value instanceof File || value instanceof Blob) {
            filesDropped = true;
            continue;
        }

        entries.push([key, String(value)]);
    }

    return { entries, filesDropped };
}

function entriesToSnapshot(entries) {
    const formData = new FormData();
    entries.forEach(([key, value]) => {
        formData.append(key, value);
    });

    return formData;
}

export function createDraftRecovery(config) {
    return {
        open: false,
        phase: 'idle',
        message: '',
        formError: '',
        pending: null,
        ceremony: null,
        filesDropped: false,

        init() {
            window.addEventListener('draft-recovery:needed', (event) => {
                this.handleNeeded(event.detail);
            });
            this.restorePersisted();
        },

        restorePersisted() {
            const contentId = config.contentId;

            if (contentId === null || contentId === undefined || contentId === '') {
                return;
            }

            let record = null;

            try {
                const raw = localStorage.getItem(storageKey(contentId));

                if (! raw) {
                    return;
                }

                record = JSON.parse(raw);
            } catch (error) {
                return;
            }

            if (! record || ! Array.isArray(record.entries) || typeof record.expiresAt !== 'number' || record.expiresAt <= Date.now()) {
                this.clearPersisted(contentId);
                return;
            }

            this.handleNeeded({
                snapshot: entriesToSnapshot(record.entries),
                contentId: record.contentId,
                ownerId: record.ownerId ?? config.ownerId ?? '',
                filesDropped: Boolean(record.filesDropped),
            });
        },

        persist(detail) {
            if (! detail?.snapshot || detail?.contentId === null || detail?.contentId === undefined || detail?.contentId === '') {
                return;
            }

            const { entries, filesDropped: detectedFilesDropped } = snapshotToEntries(detail.snapshot);
            const filesDropped = detail.filesDropped ?? detectedFilesDropped;
            const ttlMinutes = Number(config.recoveryTtlMinutes) > 0 ? Number(config.recoveryTtlMinutes) : 5;

            this.filesDropped = filesDropped;

            try {
                localStorage.setItem(storageKey(detail.contentId), JSON.stringify({
                    contentId: detail.contentId,
                    ownerId: detail.ownerId ?? '',
                    entries,
                    filesDropped,
                    expiresAt: Date.now() + ttlMinutes * 60 * 1000,
                }));
            } catch (error) {
                // localStorage unavailable or full — recovery still works in-memory for this page load.
            }
        },

        clearPersisted(contentId) {
            const id = contentId ?? this.pending?.contentId ?? config.contentId;

            if (id === null || id === undefined || id === '') {
                return;
            }

            try {
                localStorage.removeItem(storageKey(id));
            } catch (error) {
                // Ignore — nothing left to clear.
            }
        },

        csrfToken() {
            return document.querySelector('meta[name="csrf-token"]')?.content ?? '';
        },

        async handleNeeded(detail) {
            this.pending = detail;
            this.persist(detail);
            this.open = true;
            this.phase = 'bootstrapping';
            this.message = '';
            this.formError = '';

            try {
                const response = await fetch(config.bootstrapUrl, {
                    method: 'POST',
                    headers: {
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        content_id: detail.contentId,
                        owner_id: detail.ownerId,
                    }),
                });

                const payload = await response.json();

                if (! response.ok || ! payload?.recoverable) {
                    this.enterUnrecoverable(payload?.message);
                    return;
                }

                this.ceremony = {
                    startUrl: payload.start_url,
                    finishUrl: payload.finish_url,
                };
                this.message = payload.message ?? 'Confirm with your passkey to continue saving this draft.';
                this.phase = 'recoverable';
            } catch (error) {
                this.enterUnrecoverable();
            }
        },

        enterUnrecoverable(message) {
            this.clearPersisted();
            this.phase = 'unrecoverable';
            this.message = message ?? DEFAULT_UNRECOVERABLE_MESSAGE;
        },

        async startVerification() {
            if (! supportsPasskeys()) {
                this.formError = 'This browser does not support passkey verification.';
                return;
            }

            this.phase = 'passkey-in-progress';
            this.formError = '';

            try {
                const started = await this.startCeremony();

                if (started === null) {
                    return;
                }

                const credential = await navigator.credentials.get({
                    publicKey: decodeRequestOptions(started.publicKey),
                });

                if (! credential) {
                    this.phase = 'passkey-failed';
                    this.formError = 'Passkey verification was cancelled.';
                    return;
                }

                await this.finishCeremony(started.state, credential);
            } catch (error) {
                this.phase = 'passkey-failed';
                this.formError = error?.name === 'NotAllowedError'
                    ? 'Passkey verification was cancelled.'
                    : 'Passkey verification could not be completed. Try again.';
            }
        },

        async startCeremony() {
            const response = await fetch(this.ceremony.startUrl, {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': this.csrfToken(),
                    'X-Requested-With': 'XMLHttpRequest',
                },
                body: JSON.stringify({}),
            });

            const payload = await response.json();

            if (! response.ok) {
                this.phase = 'passkey-failed';
                this.formError = payload?.message ?? 'Passkey verification could not be started. Try again.';
                return null;
            }

            return payload;
        },

        async finishCeremony(state, credential) {
            const response = await fetch(this.ceremony.finishUrl, {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': this.csrfToken(),
                    'X-Requested-With': 'XMLHttpRequest',
                },
                body: JSON.stringify({
                    state,
                    credential_json: JSON.stringify(serializeAssertionCredential(credential)),
                }),
            });

            const payload = await response.json();

            if (! response.ok) {
                this.phase = 'passkey-failed';
                this.formError = payload?.errors?.passkey?.[0]
                    ?? payload?.message
                    ?? 'Passkey verification could not be completed. Try again.';
                return;
            }

            await this.replay(payload.csrf_token);
        },

        async replay(freshCsrfToken) {
            const meta = document.querySelector('meta[name="csrf-token"]');

            if (meta && typeof freshCsrfToken === 'string' && freshCsrfToken !== '') {
                meta.setAttribute('content', freshCsrfToken);
            }

            if (! this.pending?.snapshot) {
                this.enterUnrecoverable();
                return;
            }

            try {
                const response = await fetch(config.saveUrl, {
                    method: 'POST',
                    headers: {
                        Accept: 'application/json',
                        'X-CSRF-TOKEN': freshCsrfToken ?? this.csrfToken(),
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                    body: this.pending.snapshot,
                });

                const payload = await response.json().catch(() => null);

                if (! response.ok) {
                    this.enterUnrecoverable();
                    return;
                }

                window.dispatchEvent(new CustomEvent('draft-recovery:replayed', {
                    detail: { payload },
                }));

                this.clearPersisted();
                this.close();
            } catch (error) {
                this.enterUnrecoverable();
            }
        },

        close() {
            this.open = false;
            this.phase = 'idle';
            this.pending = null;
            this.ceremony = null;
            this.filesDropped = false;
        },
    };
}
